import { useRef, useState, useEffect } from "react";
import { ammanStamp } from "../lib/dates";
import type { IssueDetail, IssueEditable, IssueVersion, NewsletterSection, SectionType } from "../lib/segment-types";
import type { SyncState } from "../lib/issue-sync";
import { DEMO_SETTINGS } from "../lib/settings-data";
import { DEFAULT_UTM, utmCampaignFromTitle } from "../lib/utm";
import { runPreflight, type PreflightCheck } from "../lib/preflight";
import { useEditorHistory, useSaveState, savedLabel } from "../lib/editor-history";
import { AudiencePanel } from "./AudiencePanel";
import { SectionCard } from "./SectionCard";
import { BlockPalette } from "./BlockPalette";
import { IconCheck, IconChecklist, IconLock, IconPlusCircle, IconRedo, IconSliders, IconUndo, IconWarning } from "./icons";
import { defaultSections, emptySection, issueSections } from "../lib/sections";
import { useResolvedSections } from "../lib/use-resolved-sections";
import { Disclosure } from "./Disclosure";
import { PreviewPanel } from "./PreviewPanel";
import { categoryLabelAr } from "../lib/categories";
import { directionOf } from "../lib/text-direction";
import { Bidi, PrimaryButton, SecondaryButton } from "./primitives";
import { SendTestBox } from "./SendTestBox";

function CanvasInput({
  value,
  onChange,
  disabled,
  placeholder,
  multiline,
  rows = 2,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}) {
  const [local, setLocal] = useState(value);
  const sent = useRef(value);

  useEffect(() => {
    if (value !== sent.current) {
      setLocal(value);
      sent.current = value;
    }
  }, [value]);

  const commit = (next: string) => {
    setLocal(next);
    sent.current = next;
    onChange(next);
  };

  // dir="auto" decides from the first strong character, which gets Altibbi's
  // content wrong often: a mostly-Arabic sentence that opens with a Latin drug
  // name renders left-to-right. directionOf weighs the whole string and only
  // defers to the browser when the mix is genuinely balanced.
  const dir = directionOf(local);

  if (multiline) {
    return (
      <textarea
        className="nl-textarea"
        dir={dir}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        value={local}
        onChange={(e) => commit(e.target.value)}
      />
    );
  }

  return (
    <input
      className="nl-input"
      dir={dir}
      disabled={disabled}
      placeholder={placeholder}
      value={local}
      autoComplete="off"
      onChange={(e) => commit(e.target.value)}
    />
  );
}

/** A drag carries either a section already in the list, or a new one. */
type DragSource =
  | { kind: "move"; index: number }
  | { kind: "insert"; type: SectionType; layout?: NewsletterSection["layout"] };

/** The builder's icon rail — Add, Details, Checks — after the reference's Add / Styles / Templates. */
const RAIL = [
  { id: "add" as const, label: "Add", icon: IconPlusCircle },
  { id: "details" as const, label: "Details", icon: IconSliders },
  { id: "checks" as const, label: "Checks", icon: IconChecklist },
];

export function NewsletterEditor({
  issue,
  onChange,
  onBack,
  onSubmit,
  canSubmit,
  canApprove,
  onResolveComment,
  onRequestChanges,
  onRestoreVersion,
  onReopen,
  sync,
}: {
  issue: IssueDetail;
  /** Whether the server has caught up with what is on screen. */
  sync?: SyncState;
  onChange: (fields: IssueEditable) => void;
  onBack: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  canApprove?: boolean;
  onResolveComment?: (commentId: string) => void;
  onRequestChanges?: (comment: string, blockId?: string) => void;
  onRestoreVersion?: (versionId: string) => void;
  /** Put an in-review newsletter back into draft, in place. */
  onReopen?: () => void;
}) {
  // Sections are the newsletter's structure. Older drafts are carried over
  // from the generic block model rather than lost.
  const sections = issueSections(issue);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const categories = issue.categories ?? [];
  const [previewCategory, setPreviewCategory] = useState<string | null>(categories[0] ?? null);

  useEffect(() => {
    if (categories.length && !categories.includes(previewCategory ?? "")) {
      setPreviewCategory(categories[0]);
    }
    if (!categories.length && previewCategory) setPreviewCategory(null);
  }, [categories, previewCategory]);

  const setSections = (next: NewsletterSection[]) => {
    sectionsRef.current = next;
    onChange({ sections: next });
  };
  const updateSection = (id: string, patch: Partial<NewsletterSection>) => {
    setSections(sectionsRef.current.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };
  const [justAdded, setJustAdded] = useState<string | null>(null);
  /**
   * The footer closes the email, so nothing goes below it. A drop aimed past
   * it lands immediately before instead of being refused — the intent is
   * clear, and refusing would just feel broken.
   */
  const beforeFooter = (list: NewsletterSection[], at: number) => {
    const footer = list.findIndex((x) => x.type === "footer");
    return footer === -1 ? Math.min(at, list.length) : Math.min(at, footer);
  };

  const insertSection = (
    at: number,
    type: SectionType,
    layout?: NewsletterSection["layout"],
  ) => {
    const list = [...sectionsRef.current];
    const item = emptySection(type, layout);
    list.splice(beforeFooter(list, at), 0, item);
    setSections(list);
    // A section you have just added is a section you are about to fill in.
    setJustAdded(item.id);
  };

  /** Clicking a palette button appends; dragging one places it exactly. */
  const addSection = (type: SectionType, layout?: NewsletterSection["layout"]) => {
    insertSection(sectionsRef.current.length, type, layout);
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...sectionsRef.current];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
  };

  const locked = issue.status !== "draft";

  const moveById = (id: string, dir: -1 | 1) => {
    const index = sectionsRef.current.findIndex((x) => x.id === id);
    if (index === -1) return;
    // The footer closes the email; nothing trades places with it.
    if (sectionsRef.current[index + dir]?.type === "footer") return;
    move(index, dir);
  };

  // The builder's panel: the tile library, the newsletter's details, or the
  // checks before submitting. Selecting a block in the email swaps the panel
  // to that block's editor until "All blocks" is pressed.
  const [panel, setPanel] = useState<"add" | "details" | "checks">("add");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = sections.find((s) => s.id === selectedId && s.type !== "footer") ?? null;
  // Removing is one click, and Command-Z brings a block back.
  const removeSection = (id: string) => {
    setSections(sectionsRef.current.filter((x) => x.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  };
  // Delete removes the selected block and Escape lets go of it — unless the
  // keystroke belongs to a field being typed in.
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      // Only from the email or the page itself: a Backspace with focus on a
      // panel button is far more likely a slip than a request to delete.
      if (el && el !== document.body && !el.closest(".nl-studio-canvas")) return;
      if ((e.key === "Delete" || e.key === "Backspace") && !locked) {
        e.preventDefault();
        removeSection(selectedId);
      } else if (e.key === "Escape") {
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  // A block you have just added is a block you are about to fill in.
  useEffect(() => { if (justAdded) setSelectedId(justAdded); }, [justAdded]);
  const comments = issue.comments ?? [];
  const openComments = comments.filter((c) => !c.resolved);
  const versions = [...(issue.versions ?? [])].sort((a, b) => b.n - a.n);
  const [viewingVersion, setViewingVersion] = useState<IssueVersion | null>(null);
  // A drag either moves a section that is already here or brings a new one in
  // from the palette. Both land through the same indicator, so there is one
  // way to place a section rather than two that behave differently.
  const [drag, setDrag] = useState<DragSource | null>(null);

  // "/" opens the Add panel, the way a block editor is expected to behave.
  useEffect(() => {
    if (locked) return;
    const onSlash = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (el?.isContentEditable) return;
      e.preventDefault();
      setSelectedId(null);
      setPanel("add");
    };
    window.addEventListener("keydown", onSlash);
    return () => window.removeEventListener("keydown", onSlash);
  }, [locked]);

  const usedUrls = sections.flatMap((s) => (s.articles ?? []).map((a) => a.url)).filter(Boolean);

  const { resolved: resolvedSections, loading: resolving } =
    useResolvedSections(sections, previewCategory);

  const blocking = runPreflight(issue).checks.filter((c) => !c.ok && c.tone === "fail").length;

  const history = useEditorHistory(issue, onChange);
  const save = useSaveState(issue);

  // Command-S, Command-Return, Command-Z and Command-Shift-Z. Writing tools
  // are expected to answer these, and a block editor without undo is a trap.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === "s") {
        // Everything is already written through on change; acknowledge the
        // habit rather than letting the browser open a save dialog.
        e.preventDefault();
      } else if (k === "enter") {
        if (canSubmit && !locked) { e.preventDefault(); onSubmit(); }
      } else if (k === "z" && !e.shiftKey) {
        if (!locked) { e.preventDefault(); history.undo(); }
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        if (!locked) { e.preventDefault(); history.redo(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSubmit, locked, onSubmit, history]);

  // Viewing a version showed that version's title over today's structure,
  // which is a newsletter that never existed. It carries its own sections.
  const previewIssue = viewingVersion
    ? {
        ...issue,
        title: viewingVersion.title,
        subject: viewingVersion.subject,
        blocks: viewingVersion.blocks,
        sections: viewingVersion.sections ?? [],
        categories: viewingVersion.categories ?? [],
        notes: viewingVersion.notes,
      }
    : issue;

  const statusLabel = issue.status === "draft" ? "Draft"
    : issue.status === "in_review" ? "In review"
    : issue.status === "approved" ? "Approved"
    : issue.status === "sent" ? "Sent" : issue.status;

  const senderName = issue.sender_name ?? DEMO_SETTINGS.default_sender_name;
  const replyTo = issue.reply_to ?? DEMO_SETTINGS.default_reply_to;
  const campaign = issue.utm?.campaign || utmCampaignFromTitle(issue.title);

  return (
    <div className="nl-editor">
      {/* Where you are, what you are editing, and whether it is safe. */}
      <div className="nl-editor-bar">
        <button type="button" className="nl-crumb" onClick={onBack}>
          <span aria-hidden>←</span> Newsletters
        </button>
        <div className="nl-editor-ident">
          <h1 className="nl-editor-name"><Bidi>{issue.title || "Untitled newsletter"}</Bidi></h1>
          <div className="nl-editor-sub">
            <span className={`nl-status-dot is-${issue.status}`} aria-hidden />
            {statusLabel}
            {(save.state !== "idle" || (sync && sync.status !== "idle")) && (
              <>
                <span aria-hidden>·</span>
                <span
                  className={`nl-savestate${sync?.status === "error" ? " is-error" : ""}`}
                  role="status"
                  aria-live="polite"
                  title={sync?.status === "error" ? sync.message : undefined}
                >
                  {sync?.status === "error" ? "Saved here, not yet on the server"
                    : sync?.status === "saving" || save.state === "saving" ? "Saving…"
                    : sync?.status === "saved" ? savedLabel(sync.at)
                    : savedLabel(save.at)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="nl-editor-actions">
          {!locked && (
            <div className="nl-undo" role="group" aria-label="History">
              <button type="button" onClick={history.undo} disabled={!history.canUndo}
                aria-label="Undo" title="Undo (⌘Z)"><IconUndo /></button>
              <button type="button" onClick={history.redo} disabled={!history.canRedo}
                aria-label="Redo" title="Redo (⇧⌘Z)"><IconRedo /></button>
            </div>
          )}
          <div className="nl-submitwrap">
            <PrimaryButton
              disabled={!canSubmit || locked}
              onClick={onSubmit}
              title={canSubmit ? "Send to editorial (⌘↵)" : "Some required items are still missing"}
            >
              Submit for review
            </PrimaryButton>
            {!locked && blocking > 0 && (
              // A disabled button that does not say why is a dead end.
              <span className="nl-submitwhy">
                {blocking} required item{blocking === 1 ? "" : "s"} remaining
              </span>
            )}
          </div>
        </div>
      </div>

      <WorkflowTrail status={issue.status} />

      {/* A locked editor used to look exactly like an editable one — every
          control simply did nothing when clicked, with no explanation. */}
      {locked && (
        <div className="nl-locked" role="status">
          <IconLock className="nl-locked-mark" />
          <div>
            <strong>
              {issue.status === "in_review" ? "In review — you cannot edit this now"
                : issue.status === "approved" ? "Approved — you cannot edit this now"
                : issue.status === "sent" ? "Already sent — this is a record, not a draft"
                : "This newsletter is not editable"}
            </strong>
            <p>
              {issue.status === "in_review"
                ? canApprove
                  ? "You can reopen it for editing — that sends it back to the author as a draft."
                  : "An editor is reviewing it. It becomes editable again if they request changes."
                : issue.status === "approved"
                  ? "It is waiting to go into a campaign. Ask an editor to reopen it if it needs a change."
                  : issue.status === "sent"
                    ? "Sent newsletters are kept as they went out. Start a new one to reuse this structure."
                    : "Only drafts can be edited."}
            </p>
          </div>
          {/* A page that explains why it is dead is still dead. Where the app
              has a way out, offer it here rather than making people find it. */}
          {issue.status === "in_review" && canApprove && onReopen && (
            <button type="button" className="nl-locked-action" onClick={onReopen}>
              Reopen for editing
            </button>
          )}
        </div>
      )}

      {openComments.length > 0 && (
        <div className="nl-editor-feedback">
          <div className="nl-stat-mini-label">Editor feedback</div>
          {openComments.map((c) => (
            <div key={c.id} className="nl-feedback-item">
              <blockquote>{c.text}</blockquote>
              <div className="nl-feedback-meta">{c.author} · {ammanStamp(c.at)}</div>
              {onResolveComment && issue.status === "draft" && (
                <SecondaryButton onClick={() => onResolveComment(c.id)}>Resolve</SecondaryButton>
              )}
            </div>
          ))}
        </div>
      )}

      {/* A three-part builder: an icon rail, a panel, and the email itself as
          the canvas. Click a block in the email to edit it; click or drag a
          tile to add one. Structure after Mailchimp's builder, look from the
          Altibbi tool standard. */}
      <div className={`nl-studio${locked ? " is-readonly" : ""}`}>
        <nav className="nl-studio-rail" aria-label="Editor panels">
          <div className="nl-studio-rail-inner">
          {RAIL.map((r) => (
            <button
              key={r.id}
              type="button"
              className="nl-rail-btn"
              aria-pressed={panel === r.id && !selected}
              onClick={() => { setPanel(r.id); setSelectedId(null); }}
            >
              <r.icon width={22} height={22} strokeWidth={1.3} />
              <span>{r.label}</span>
            </button>
          ))}
          </div>
        </nav>

        <div className="nl-studio-panel">
          <div className="nl-studio-panel-inner">
          {selected ? (
            <div className="nl-blockedit">
              <button type="button" className="nl-blockedit-back" onClick={() => setSelectedId(null)}>
                ← All blocks
              </button>
              <SectionCard
                key={selected.id}
                section={selected}
                audience={categories}
                locked={locked}
                usedInIssue={usedUrls}
                defaultOpen
                onChange={(patch) => updateSection(selected.id, patch)}
                onRemove={() => removeSection(selected.id)}
                note={comments.find((c) => c.block_id === selected.id && !c.resolved) ?? null}
                canComment={canApprove && issue.status === "in_review"}
                onRequestChanges={onRequestChanges}
                onResolveComment={onResolveComment}
              />
            </div>
          ) : panel === "add" ? (
            <>
              {sections.length === 0 && !locked && (
                <div className="nl-block-empty">
                  <p>This newsletter has no structure yet.</p>
                  <button type="button" className="nl-btn-secondary" onClick={() => setSections(defaultSections())}>
                    Use the standard structure
                  </button>
                </div>
              )}
              <BlockPalette
                sections={sections}
                locked={locked}
                onAdd={(type, layout) => addSection(type, layout)}
                onDragStart={(type, layout) => setDrag({ kind: "insert", type, layout })}
                onDragEnd={() => setDrag(null)}
                onSelect={setSelectedId}
                onMove={moveById}
                onRemove={removeSection}
              />
            </>
          ) : panel === "details" ? (
            <div className="nl-panel-stack">
                <div id="nl-audience">
                <AudiencePanel
                  categories={categories}
                  locked={locked}
                  onChange={(next) => onChange({ categories: next })}
                />
                </div>
              <section className="nl-canvas-section" id="nl-details" aria-label="Newsletter details">
                <div className="nl-grouphead">
                  <span className="nl-sectionnum" aria-hidden>2</span>
                  <div>
                    <h2>What the inbox shows</h2>
                  </div>
                </div>

                {/* One column, one field width, one input size. These were a
                    full-width subject, a half-width preheader and a full-width
                    name, which made three equal fields look like three
                    different kinds of thing. */}
                <div className="nl-fieldstack">
                  <SubjectField
                    label="Email subject"
                    hint="Your subscribers see this in their inbox."
                    placeholder="أبرز مقالات الطبي هذا الأسبوع"
                    value={issue.subject}
                    disabled={locked}
                    ideal={[28, 100]}
                    onChange={(subject) => onChange({ subject })}
                  />

                  <SubjectField
                    label="Preheader"
                    hint="The line after the subject in the inbox."
                    placeholder="نصائح ومقالات صحية مختارة لك هذا الأسبوع…"
                    value={issue.preheader ?? ""}
                    disabled={locked}
                    ideal={[40, 100]}
                    onChange={(preheader) => onChange({ preheader })}
                  />

                  <SubjectField
                    label="Internal name"
                    hint="Only your team sees this."
                    placeholder="September — weekly newsletter"
                    value={issue.title}
                    disabled={locked}
                    ideal={[20, 60]}
                    onChange={(title) => onChange({ title })}
                  />
                </div>
              </section>
              <section className="nl-canvas-section" id="nl-sending" aria-label="Sending and tracking">
                <div className="nl-grouphead">
                  <span className="nl-sectionnum" aria-hidden>4</span>
                  <div>
                    <h2>Sending &amp; tracking</h2>
                    <p>Who it comes from, and how its clicks are reported. Set once and rarely changed.</p>
                  </div>
                </div>

                <Disclosure
                  label="Show sending details"
                  summary={`${senderName} · ${replyTo}`}
                >
                  <div className="nl-settinggrid">
                    <label className="nl-field nl-field-explained">
                      <span>Sender name</span>
                      <CanvasInput disabled={locked} value={senderName} onChange={(sender_name) => onChange({ sender_name })} />
                      <em className="nl-field-hint">Shown as the sender in the inbox.</em>
                    </label>

                    <label className="nl-field nl-field-explained">
                      <span>Reply-to</span>
                      <CanvasInput disabled={locked} value={replyTo} onChange={(reply_to) => onChange({ reply_to })} />
                      <em className="nl-field-hint">Where a reader's reply lands.</em>
                    </label>

                    <label className="nl-field nl-field-explained">
                      <span>Campaign</span>
                      <CanvasInput
                        disabled={locked}
                        value={campaign}
                        onChange={(c) => onChange({ utm: { ...DEFAULT_UTM, ...issue.utm, campaign: c } })}
                      />
                      <em className="nl-field-hint">
                        The name this send is reported under. Taken from the internal name —
                        change it only if this issue needs its own reporting bucket.
                      </em>
                    </label>

                    <div className="nl-field nl-field-explained">
                      <span>Tracking</span>
                      <div className="nl-field-readonly">
                        <span>utm_source</span><code>{issue.utm?.source ?? DEFAULT_UTM.source}</code>
                      </div>
                      <div className="nl-field-readonly">
                        <span>utm_medium</span><code>{issue.utm?.medium ?? DEFAULT_UTM.medium}</code>
                      </div>
                      <em className="nl-field-hint">Fixed for every newsletter send.</em>
                    </div>

                    <label className="nl-field nl-field-explained">
                      <span>Notes for the editor</span>
                      <CanvasInput
                        multiline
                        disabled={locked}
                        value={issue.notes ?? ""}
                        onChange={(notes) => onChange({ notes })}
                      />
                      <em className="nl-field-hint">
                        Context for whoever reviews this. Never sent to subscribers.
                        Use {"{{first_name}}"} in the newsletter itself to greet a reader by name.
                      </em>
                    </label>
                  </div>
                </Disclosure>
              </section>
            </div>
          ) : (
            <div className="nl-panel-stack">

              <ReadinessSummary
                issue={issue}
                onJump={(anchor) => {
                  // The fix lives in another panel: open it, then bring the
                  // field into view once it has rendered.
                  setSelectedId(null);
                  setPanel(anchor === "content" ? "add" : "details");
                  requestAnimationFrame(() => {
                    const id = anchor === "content" ? "nl-canvas" : `nl-${anchor}`;
                    document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "smooth" });
                  });
                }}
              />

              <Disclosure label="Send a test">
                <SendTestBox
                  issue={issue}
                  sections={resolvedSections}
                  categoryHeading={previewCategory ? categoryLabelAr(previewCategory) : undefined}
                />
              </Disclosure>

              {versions.length > 0 && (
                <Disclosure label="Version history" summary={`${versions.length}`}>
                  <VersionHistory
                    versions={versions}
                    viewingId={viewingVersion?.id ?? null}
                    canRestore={issue.status === "draft"}
                    onView={(v) => setViewingVersion((cur) => cur?.id === v.id ? null : v)}
                    onRestore={(id) => {
                      onRestoreVersion?.(id);
                      setViewingVersion(null);
                    }}
                  />
                </Disclosure>
              )}
            </div>
          )}
          </div>
        </div>

        <div className="nl-studio-canvas" id="nl-canvas">
          <PreviewPanel
            issue={previewIssue}
            // A version is shown as it was: its own sections, not today's
            // resolved ones. Its dynamic slots are whatever it had at the time.
            sections={viewingVersion ? (viewingVersion.sections ?? []) : resolvedSections}
            categories={categories}
            previewCategory={previewCategory}
            onPreviewCategory={setPreviewCategory}
            resolving={resolving}
            canvas={viewingVersion ? undefined : {
              selectedId,
              dragging: Boolean(drag),
              onSelect: (id) => { setSelectedId(id); if (id) setPanel("add"); },
              onDrop: (index) => {
                if (drag?.kind === "insert") insertSection(index, drag.type, drag.layout);
                setDrag(null);
              },
              onMove: moveById,
              onRemove: removeSection,
              locked,
            }}
          />
        </div>
      </div>
    </div>
  );
}

/** Write → Preview → Review → Schedule → Sent, with the real stage marked. */
function WorkflowTrail({ status }: { status: IssueDetail["status"] }) {
  const stages = ["Write", "Preview", "Review", "Schedule", "Sent"] as const;
  const reached =
    status === "sent" ? 4
    : status === "approved" ? 3
    : status === "in_review" ? 2
    : 0;
  return (
    <ol className="nl-trail" aria-label="Newsletter workflow">
      {stages.map((stage, i) => (
        <li
          key={stage}
          className={i < reached ? "is-done" : i === reached ? "is-active" : ""}
          aria-current={i === reached ? "step" : undefined}
        >
          {stage}
        </li>
      ))}
    </ol>
  );
}

/**
 * Readiness, stated as the work that is left.
 *
 * This used to read "2 to fix" with the list folded away, so the one question
 * an editor actually has — what is wrong — took a click to answer.
 */
function ReadinessSummary({
  issue,
  onJump,
}: {
  issue: IssueDetail;
  onJump: (anchor: NonNullable<PreflightCheck["anchor"]>) => void;
}) {
  const checks = runPreflight(issue).checks;
  const blocking = checks.filter((c) => !c.ok && c.tone === "fail");
  const warnings = checks.filter((c) => c.tone === "warn");
  const ready = blocking.length === 0;

  const jump = (anchor?: PreflightCheck["anchor"]) => {
    if (anchor) onJump(anchor);
  };

  return (
    <div className={`nl-readiness${ready ? " is-ready" : ""}`}>
      <div className="nl-readiness-head">
        <span className="nl-readiness-mark" aria-hidden>{ready ? <IconCheck /> : <IconWarning />}</span>
        <span className="nl-readiness-label">
          {ready
            ? warnings.length
              ? `Ready to submit · ${warnings.length} suggestion${warnings.length === 1 ? "" : "s"}`
              : "Ready to submit"
            : `${blocking.length} ${blocking.length === 1 ? "item" : "items"} before you can submit`}
        </span>
      </div>

      {blocking.length > 0 && (
        <ul className="nl-issues">
          {blocking.map((c) => (
            <li key={c.id}>
              <span className="nl-issues-mark is-fail" aria-hidden><IconWarning /></span>
              <div>
                <strong>{c.label}</strong>
                {c.fix && <span>{c.fix}</span>}
                {c.detail && <em>{c.detail}</em>}
              </div>
              {c.anchor && (
                <button type="button" className="nl-issues-go" onClick={() => jump(c.anchor)}>
                  Go
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {warnings.length > 0 && (
        <ul className="nl-issues is-warnings">
          {warnings.map((c) => (
            <li key={c.id}>
              <span className="nl-issues-mark is-warn" aria-hidden>•</span>
              <div>
                <strong>{c.label}</strong>
                {c.fix && <span>{c.fix}</span>}
              </div>
              {c.anchor && (
                <button type="button" className="nl-issues-go" onClick={() => jump(c.anchor)}>
                  Go
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {ready && warnings.length === 0 && (
        <p className="nl-readiness-allclear">
          Every pre-send check passes. An editor reviews it next.
        </p>
      )}
    </div>
  );
}

/** A single-line field that reports its own length against an ideal range. */
function SubjectField({
  label, hint, value, placeholder, disabled, ideal, onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  ideal: [number, number];
  onChange: (v: string) => void;
}) {
  const len = value.trim().length;
  const [min, max] = ideal;
  // A bare count answers "how much room is left"; the tone answers "is this
  // a good length", which is the question an editor actually has.
  const state = len === 0 ? "empty" : len < min ? "short" : len > max ? "long" : "good";
  return (
    <div className="nl-subjectfield">
      <div className="nl-subjectfield-head">
        <label className="nl-subjectfield-label">{label}</label>
        <span
          className={`nl-subjectfield-count is-${state}`}
          title={state === "long" ? "May be cut off in the inbox"
            : state === "good" ? "Good length" : `${min}–${max} characters`}
        >{len}/{max}</span>
      </div>
      <CanvasInput disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />
      <div className="nl-subjectfield-foot">
        {hint && <span className="nl-subjectfield-hint">{hint}</span>}
        {len > 0 && (
          <span className={`nl-subjectfield-verdict is-${state}`}>
            {state === "good" ? <><IconCheck /> Good length</>
              : state === "short" ? `Short — aim for ${min}+ characters`
              : "May be cut off in the inbox"}
          </span>
        )}
      </div>
    </div>
  );
}

function VersionHistory({
  versions, viewingId, canRestore, onView, onRestore,
}: {
  versions: IssueVersion[];
  viewingId: string | null;
  canRestore: boolean;
  onView: (v: IssueVersion) => void;
  onRestore: (id: string) => void;
}) {
  return (
    <div className="nl-versions">
      <div className="nl-stat-mini-label">Version history</div>
      <ol>
        {versions.map((v) => (
          <li key={v.id} className={viewingId === v.id ? "is-active" : ""}>
            <button type="button" onClick={() => onView(v)}>
              <strong>v{v.n} — {v.status.replace("_", " ")}</strong>
              <span>{ammanStamp(v.at)} — {v.actor}</span>
            </button>
            {viewingId === v.id && (
              <div className="nl-version-actions">
                <span>Viewing previous version</span>
                {canRestore && <SecondaryButton onClick={() => onRestore(v.id)}>Restore this version</SecondaryButton>}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

